// GoalDetailPage — /agent/goals/:id
// Shows goal header, progress-notes timeline, run history, and archive button.

import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getGoal, listGoalRuns, archiveGoal, formatGoalCadence, formatNextRun,
  type Goal, type GoalRun,
} from '../../api/goals';
import GoalAuthorizationSection from '../../components/AgentChat/GoalAuthorizationSection';
import { useCanWriteAgent } from '../../hooks/useCanWriteAgent';
import styles from './GoalDetailPage.module.css';

const STATUS_LABEL: Record<string, string> = {
  active: 'Active',
  completed: 'Done',
  paused: 'Paused',
  abandoned: 'Abandoned',
};

const RUN_ICON: Record<string, string> = {
  completed: '✅',
  failed: '❌',
  needs_approval: '⏳',
  running: '🔄',
};

const APPROVAL_LABEL: Record<string, string> = {
  pending: 'Pending',
  approved: 'Approved',
  rejected: 'Rejected',
  auto_applied: 'Auto-applied',
};

function RunRow({ run }: { run: GoalRun }) {
  const dur =
    run.started_at && run.finished_at
      ? Math.round((new Date(run.finished_at).getTime() - new Date(run.started_at).getTime()) / 1000)
      : null;

  return (
    <tr className={styles.runRow}>
      <td className={styles.runIcon}>{RUN_ICON[run.status] ?? '•'}</td>
      <td>
        <span className={`${styles.runStatus} ${styles[`run_${run.status}`]}`}>{run.status}</span>
      </td>
      <td>
        {run.approval_status ? (
          <span className={`${styles.approvalBadge} ${styles[`approval_${run.approval_status}`] ?? ''}`}>
            {APPROVAL_LABEL[run.approval_status] ?? run.approval_status}
          </span>
        ) : '—'}
      </td>
      <td className={styles.runCredits}>{run.credits_spent > 0 ? run.credits_spent : '—'}</td>
      <td className={styles.runDate}>
        {run.started_at ? new Date(run.started_at).toLocaleString() : '—'}
      </td>
      <td className={styles.runDur}>{dur != null ? `${dur}s` : '—'}</td>
      <td>
        {run.session_id ? (
          <a className={styles.transcriptLink} href={`/agent?session=${run.session_id}`}>
            View transcript
          </a>
        ) : '—'}
      </td>
    </tr>
  );
}

function parseNumeric(value: string | null): number | null {
  if (!value) return null;
  const cleaned = value.replace(/[%,#]/g, '').trim();
  if (!cleaned) return null;
  const n = parseFloat(cleaned);
  return Number.isFinite(n) ? n : null;
}

/** Progress toward target as a bar. Direction-agnostic: works whether the
 * metric improves by going up (traffic) or down (average rank position),
 * as long as a baseline is set — without one it just shows current/target. */
function GoalProgressBar({ goal }: { goal: Goal }) {
  const current = goal.current_value_num ?? parseNumeric(goal.current_value);
  const target = parseNumeric(goal.target_value);
  const baseline = parseNumeric(goal.baseline_value);
  if (current == null || target == null) return null;

  let pct: number;
  if (baseline != null && target !== baseline) {
    pct = ((current - baseline) / (target - baseline)) * 100;
  } else if (target !== 0) {
    pct = (current / target) * 100;
  } else {
    return null;
  }
  pct = Math.max(0, Math.min(100, pct));

  return (
    <div className={styles.progressBarWrap}>
      <div className={styles.progressBarTrack}>
        <div className={styles.progressBarFill} style={{ width: `${pct}%` }} />
      </div>
      <span className={styles.progressBarLabel}>{Math.round(pct)}% of target</span>
    </div>
  );
}

export default function GoalDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const canWrite = useCanWriteAgent();
  const [archiving, setArchiving] = useState(false);
  const [archiveError, setArchiveError] = useState<string | null>(null);

  const { data: goal, isLoading: goalLoading, error: goalError } = useQuery<Goal>({
    queryKey: ['goal', id],
    queryFn: () => getGoal(id!),
    enabled: !!id,
  });

  const { data: runs, isLoading: runsLoading } = useQuery<GoalRun[]>({
    queryKey: ['goal-runs', id],
    queryFn: () => listGoalRuns(id!),
    enabled: !!id,
    staleTime: 30_000,
  });

  async function handleArchive() {
    if (!id) return;
    setArchiving(true);
    setArchiveError(null);
    try {
      await archiveGoal(id);
      qc.invalidateQueries({ queryKey: ['goals'] });
      qc.invalidateQueries({ queryKey: ['goal', id] });
      navigate('/agent');
    } catch (e) {
      setArchiveError((e as Error).message || 'Failed to archive goal.');
      setArchiving(false);
    }
  }

  if (goalLoading) {
    return <div className={styles.page}><p className={styles.loading}>Loading goal…</p></div>;
  }

  if (goalError || !goal) {
    return (
      <div className={styles.page}>
        <button className={styles.backBtn} onClick={() => navigate('/agent')}>← Back</button>
        <p className={styles.error}>Goal not found.</p>
      </div>
    );
  }

  const notes = goal.progress_notes ?? [];
  const cadence = formatGoalCadence(goal);
  const nextRun = formatNextRun(goal);

  return (
    <div className={styles.page}>
      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div className={styles.topBar}>
        <button className={styles.backBtn} onClick={() => navigate('/agent')}>← Back</button>
        {goal.status === 'active' && canWrite && (
          <button className={styles.archiveBtn} onClick={handleArchive} disabled={archiving}>
            {archiving ? 'Archiving…' : 'Archive goal'}
          </button>
        )}
      </div>

      {archiveError && <p className={styles.error}>{archiveError}</p>}

      <div className={styles.header}>
        <div className={styles.headerMeta}>
          <span className={`${styles.statusBadge} ${styles[`status_${goal.status}`]}`}>
            {STATUS_LABEL[goal.status] ?? goal.status}
          </span>
          {goal.template_key && (
            <span className={styles.templateTag}>{goal.template_key.replace(/_/g, ' ')}</span>
          )}
        </div>
        <h1 className={styles.title}>{goal.title}</h1>
        {goal.description && <p className={styles.description}>{goal.description}</p>}
        <div className={styles.statRow}>
          {goal.target_metric && (
            <span className={styles.stat}>
              <span className={styles.statLabel}>Metric</span> {goal.target_metric}
            </span>
          )}
          {goal.current_value && (
            <span className={styles.stat}>
              <span className={styles.statLabel}>Current</span> {goal.current_value}
            </span>
          )}
          {goal.target_value && (
            <span className={styles.stat}>
              <span className={styles.statLabel}>Target</span> {goal.target_value}
            </span>
          )}
          {cadence && (
            <span className={styles.stat}>
              <span className={styles.statLabel}>Cadence</span> {cadence}
              {goal.schedule_enabled === false ? ' (paused)' : ''}
            </span>
          )}
          {nextRun && (
            <span className={styles.stat}>
              <span className={styles.statLabel}>Next run</span> {nextRun}
            </span>
          )}
          {goal.last_run_at && (
            <span className={styles.stat}>
              <span className={styles.statLabel}>Last run</span>{' '}
              {new Date(goal.last_run_at).toLocaleString()}
            </span>
          )}
        </div>
        <GoalProgressBar goal={goal} />
      </div>

      {/* ── Authorization & credits ──────────────────────────────────────────── */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>Authorization &amp; credits</h2>
        <GoalAuthorizationSection
          goal={goal}
          onUpdated={() => qc.invalidateQueries({ queryKey: ['goal', id] })}
        />
      </section>

      {/* ── Progress notes timeline ─────────────────────────────────────────── */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>Progress notes</h2>
        {notes.length === 0 ? (
          <p className={styles.emptyHint}>No progress notes yet — the agent will add them after each run.</p>
        ) : (
          <ol className={styles.timeline}>
            {[...notes].reverse().map((n, i) => (
              <li key={i} className={styles.timelineItem}>
                <span className={styles.noteDate}>{n.date}</span>
                <p className={styles.noteText}>{n.note}</p>
              </li>
            ))}
          </ol>
        )}
      </section>

      {/* ── Run history ─────────────────────────────────────────────────────── */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>Run history</h2>
        {runsLoading && <p className={styles.loading}>Loading runs…</p>}
        {!runsLoading && runs && runs.length === 0 && (
          <p className={styles.emptyHint}>No runs yet — the first run will start on its next scheduled time.</p>
        )}
        {!runsLoading && runs && runs.length > 0 && (
          <div className={styles.tableWrap}>
            <table className={styles.runTable}>
              <thead>
                <tr>
                  <th />
                  <th>Status</th>
                  <th>Approval</th>
                  <th>Credits</th>
                  <th>Started</th>
                  <th>Duration</th>
                  <th>Transcript</th>
                </tr>
              </thead>
              <tbody>
                {runs.map(r => <RunRow key={r.id} run={r} />)}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
